import { useQuery } from '@apollo/client';

import user from 'js/lib/user';

import type { ProductOwnershipsReturnType } from 'bundles/enroll/hooks/useProductOwnerships';
import useProductOwnerships from 'bundles/enroll/hooks/useProductOwnerships';
import GetS12nIdsByCourseId from 'bundles/enroll/queries/GetS12nIdsByCourseId.graphql';
import type {
  GetS12nIdsByCourseIdQuery as GetS12nIdsByCourseIdQueryData,
  GetS12nIdsByCourseIdQueryVariables,
} from 'bundles/enroll/queries/__generated__/GetS12nIdsByCourseId';
import { isCourseGrantedByFreeTrial } from 'bundles/enroll/utils/withCourseOwnership';
import { useNaptime } from 'bundles/naptimejs';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for this module.
import SubscriptionTrialsV1 from 'bundles/naptimejs/resources/subscriptionTrials.v1';

type PropsFromSubscriptionTrials = {
  subscriptionTrials?: Array<SubscriptionTrialsV1>;
};

type CourseOwnershipReturnType = {
  loading?: boolean;
  course: { s12nIds: Array<string> };
  ownsCourse: boolean;
  isFreeTrial?: boolean;
  subscriptionTrials?: Array<SubscriptionTrialsV1>;
  courseOwnership: ProductOwnershipsReturnType['courseOwnership'];
};

export const useGetCourseS12nIds = (courseId: string) => {
  const { data, loading } = useQuery<GetS12nIdsByCourseIdQueryData, GetS12nIdsByCourseIdQueryVariables>(
    GetS12nIdsByCourseId,
    {
      variables: {
        id: courseId,
      },
      skip: !user.isAuthenticatedUser() || !courseId,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const course = data?.Course?.queryById;
  const s12nIds = course?.specializations?.map(({ id }) => id) ?? [];

  return { ...course, s12nIds, loading };
};

const useGetSubscriptionTrials = (ownsCourse: boolean) => {
  const {
    data: { subscriptionTrials },
    pending,
  } = useNaptime<PropsFromSubscriptionTrials>(() => {
    if (!user.isAuthenticatedUser() || !ownsCourse) {
      return {};
    }

    return {
      subscriptionTrials: ownsCourse
        ? SubscriptionTrialsV1.finder('findByUser', {
            params: {
              userId: user.get().id,
            },
            fields: ['createdAt', 'endsAt', 'productType', 'productItemId'],
          })
        : undefined,
    };
  }, [ownsCourse]);

  return {
    subscriptionTrials,
    loading: pending,
  };
};

/**
 * Custom hook to replace `withCourseOwnership` HOC.
 * @param courseId
 * @returns {CourseOwnershipReturnType}
 */
const useCourseOwnership = (courseId: string): CourseOwnershipReturnType => {
  const { courseOwnership, loading: loadingCourseOwnership } = useProductOwnerships({ courseId });
  const ownsCourse = courseOwnership?.owns ?? false;

  const course = useGetCourseS12nIds(courseId);

  const { subscriptionTrials, loading: loadingSubscriptionTrials } = useGetSubscriptionTrials(ownsCourse);

  const isFreeTrial =
    subscriptionTrials && isCourseGrantedByFreeTrial(courseId, course.s12nIds || [], subscriptionTrials);

  return {
    loading: loadingCourseOwnership || loadingSubscriptionTrials || course?.loading,
    course,
    ownsCourse,
    isFreeTrial,
    subscriptionTrials,
    courseOwnership,
  };
};

export default useCourseOwnership;
